import * as Sentry from "@sentry/react";
import useScrollToTop from "core-integration/src/hooks/useScrollToTop";
import { ThunkDispatch } from "core-integration/src/store";
import { getBootstrapped } from "core-integration/src/store/bootstrap/reducers";
import { bootstrap } from "core-integration/src/store/bootstrap/thunks";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import { getServerError } from "core-integration/src/store/global/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { fetchRegions } from "core-integration/src/store/regions/thunks";
import { User } from "oidc-client-ts";
import "plos/dist/tokens.css";
import FixtureBroadcasters from "plos/src/components/Fixtures/Fixture/FixtureBroadcasters";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { PlosTranslationsProvider } from "plos/src/contexts/PlosTranslationsContext";
import { WebViewContext } from "plos/src/contexts/WebViewContext";
import { isPLOnboardingActive } from "plos/src/hooks/useIsPLOnboardingOpen";
import { useLanguageFromPath } from "plos/src/hooks/useLanguageFromPath";
import { maxWidthContainer } from "plos/src/layouts";
import "plos/src/styles/globals.css";
import { darkTheme, lightTheme } from "plos/src/styles/theme.css";
import * as React from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { UNSAFE_PortalProvider } from "react-aria";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useDispatch, useSelector } from "react-redux";
import { getCookie } from "shared-utils/src/cookies";
import { isRTL } from "shared-utils/src/locale";
import { clearPingCookies, getSSOCookieName } from "shared-utils/src/ping";
import { AdobeProvider, useAdobeContext } from "../contexts/AdobeContext";
import { OnboardingProvider } from "../contexts/OnboardingContext";
import { TrackingProvider } from "../contexts/TrackingContext";
import "../fonts.css";
import { getPlosTranslations } from "../translations/plosTranslations";
import { AdobePopupContainer } from "./AdobePopupContainer";
import { CrestSheet } from "./crestSheets/CrestSheet";
import GameHeader from "./GameHeader";
import { AutoJoinDialog } from "./leagues/AutoJoinDialog";
import { getUser, u13userManager, userManager } from "./ping/PingAuthProvider";
import { PlayerProfileSheet } from "./PlayerSheets/PlayerProfileSheet";
import Routes from "./Routes";
import ServerError from "./ServerError";

declare global {
  interface Window {
    getUser?: () => Partial<User> | null;
  }
}

export const Loading = () => {
  const { t } = useTranslation();
  return (
    <SurfaceContainer>
      <p>{t("loading")}</p>
    </SurfaceContainer>
  );
};

// The amount of time in minutes prior to a gameweek deadline that
// we allow onboarding redirecting to occur
const onboardingMitigation = 0;

const App = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch>();
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const themeRootRef = useRef<HTMLDivElement>(null);
  const overlayPortalRef = useRef<HTMLDivElement>(null);
  const { isWebView } = useContext(WebViewContext);
  const { ready } = useAdobeContext();
  const language = useLanguageFromPath();

  const bootstrapped = useSelector(getBootstrapped);
  const nextEvent = useSelector(getNextEvent);
  const player = useSelector(getPlayerData);
  const serverError = useSelector(getServerError);

  const auth = useAuth();

  const isLogoutReturn =
    new URLSearchParams(window.location.search).get("logout") === "true";

  // Runs once, on the logout return only
  React.useEffect(() => {
    if (!isLogoutReturn) return;

    clearPingCookies();

    window.location.replace(
      `${
        import.meta.env.VITE_OIDC_AUTHORITY
      }/signoff?post_logout_redirect_uri=${encodeURIComponent(
        window.location.origin,
      )}`,
    );
  }, []);

  // Expose getUser to window
  React.useEffect(() => {
    (async () => {
      const user = await getUser();
      window.getUser = () => {
        if (user) {
          return {
            access_token: user?.access_token,
            profile: user?.profile,
          };
        } else {
          return null;
        }
      };
    })();

    return () => {
      delete window.getUser;
    };
  }, [auth.user]);

  // Once we receive the FPL_ONBOARDING_COMPLETE event we call
  // signinSilent to refresh the data in localStorage. If the user
  // is under age (13), we sign them out as they await guardian approval
  React.useEffect(() => {
    const handleOnboardingComplete = async (event: Event) => {
      const customEvent = event as CustomEvent<{ isUnderAge: boolean }>;
      const isUnderAge = customEvent.detail?.isUnderAge;

      if (isUnderAge) {
        const accessToken = auth.user && auth.user.access_token;
        if (accessToken) {
          clearPingCookies();
          await userManager.removeUser();
          await u13userManager.signinRedirect({
            redirect_uri: `${window.location.origin}/?logout=true`,
            extraQueryParams: {
              logout: "true",
              u13token: accessToken,
            },
          });
        }
      } else {
        await userManager.signinSilent();
      }
    };

    document.addEventListener(
      "FPL_ONBOARDING_COMPLETE",
      handleOnboardingComplete,
    );

    return () => {
      document.removeEventListener(
        "FPL_ONBOARDING_COMPLETE",
        handleOnboardingComplete,
      );
    };
  }, [auth.user]);

  const ssoCookieName = getSSOCookieName(isWebView);
  const global_sso_id = ssoCookieName ? getCookie(ssoCookieName) : "";

  useEffect(() => {
    let cancelled = false;

    if (auth.isLoading || !bootstrapped) {
      return;
    }

    (async () => {
      const user = await getUser();

      if (cancelled) return;

      // Seamless Login
      if (global_sso_id) {
        if (!user) {
          Sentry.logger.info(
            "No oidc user but global_sso_id cookie present, seamless login started",
            {
              event_type: "seamless_login",
            },
          );
          await auth.signinRedirect({
            extraQueryParams: { language },
          });
          return;
        }

        if (user.profile.sub !== global_sso_id) {
          Sentry.logger.info(
            "SSO id mismatch, oidc user removed from localStorage and login retried",
            {
              event_type: "sso_id_mismatch",
            },
          );
          await auth.removeUser();
          await auth.signinRedirect({
            extraQueryParams: { language },
          });
          return;
        }
      }

      // No user, nothing else to check
      if (!user) return;

      // Onboarding
      if (user.profile.onboarded === "false") {
        if (import.meta.env.VITE_PL_ONBOARDING_ENABLED !== "true") {
          return;
        }

        let showOnboarding = false;
        if (onboardingMitigation > 0) {
          if (nextEvent) {
            // Check if the current time is not within the set onboardingMitigation limit.
            // If so redirect to the respective premierleague.com instance.
            const deadlineTime = new Date(nextEvent.deadline_time);
            const now = new Date();
            showOnboarding =
              now <=
              new Date(
                deadlineTime.getTime() - onboardingMitigation * 60 * 1000,
              );
          } else {
            showOnboarding = true;
          }
        } else {
          showOnboarding = true;
        }

        if (showOnboarding && window.getUser) {
          // ONBOARDING.TRIGGER toggles the PL modal, which may already have opened itself
          if (!isPLOnboardingActive()) {
            document.dispatchEvent(new CustomEvent("ONBOARDING.OPEN"));
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [auth.isLoading, bootstrapped, global_sso_id]);

  // Seamless Login Logout
  useEffect(() => {
    if (ssoCookieName) {
      if (!auth.isLoading) {
        if (player && global_sso_id === "") {
          Sentry.logger.info(
            "Logged in player with no global_sso_id, user logged out from Ping",
            {
              event_type: "missing_global_sso_id",
            },
          );
          window.branch?.logout();
          auth.signoutRedirect({
            post_logout_redirect_uri: `${window.location.origin}/`,
          });
        }
      }
    }
  }, [auth.isLoading, global_sso_id, player]);

  useEffect(() => {
    if (!auth.isLoading) {
      // Bootstrap on initial render and when user changes (this handles when we redirect after Ping signin)
      dispatch(bootstrap());
      dispatch(fetchRegions());
    }
  }, [dispatch, auth.isLoading, auth.user]);

  useEffect(() => {
    // Check for system preference
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    setTheme(prefersDark ? "dark" : "light");

    // Set up a listener for preference changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setTheme(e.matches ? "dark" : "light");
    };

    // Prefer modern API, fallback to old one
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      // Legacy API for older browsers/test environments
      (mediaQuery as any).addListener(handleChange);
    }

    // Clean up
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        // Legacy API for older browsers/test environments
        (mediaQuery as any).removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    themeRootRef.current?.setAttribute("data-theme", theme);
  }, [theme]);

  // Bootstrapped & Adobe SDK is ready (whether initialized or not)
  const loaded = !auth.isLoading && bootstrapped && ready;

  // Handle scroll restoration
  useScrollToTop();

  const vanillaExtractTheme = theme === "dark" ? darkTheme : lightTheme;

  return (
    <React.StrictMode>
      <div className={vanillaExtractTheme}>
        <div
          className="ism"
          ref={themeRootRef}
          dir={isRTL(i18n.language) ? "rtl" : "ltr"}
        >
          <AdobePopupContainer />
          <UNSAFE_PortalProvider getContainer={() => overlayPortalRef.current}>
            {!isWebView && <GameHeader />}
            <div className={maxWidthContainer}>
              {serverError ? (
                <ServerError error={serverError} />
              ) : loaded ? (
                <OnboardingProvider>
                  <FixtureBroadcasters>
                    <Routes />
                  </FixtureBroadcasters>
                </OnboardingProvider>
              ) : (
                <Loading />
              )}
            </div>
            <AutoJoinDialog />
            <PlayerProfileSheet />
            <CrestSheet />
          </UNSAFE_PortalProvider>
          <div id="overlay-portal" ref={overlayPortalRef} />
        </div>
      </div>
    </React.StrictMode>
  );
};

const AppContainer = () => {
  const { t } = useTranslation("plos");

  return (
    <AdobeProvider>
      <TrackingProvider>
        <PlosTranslationsProvider translations={getPlosTranslations(t)}>
          <App />
        </PlosTranslationsProvider>
      </TrackingProvider>
    </AdobeProvider>
  );
};

export default AppContainer;
